import { CloseIcon, LazyImage, Logo, Select } from '@mr-yum/frontend-ui'
import cn from 'classnames'
import { Meta } from 'components/Meta/Meta'
import { useDirectoryVenueCollectionsQuery } from 'lib/gql'
import find from 'lodash/find'
import { useRouter } from 'next/router'
import React, { PropsWithChildren, useCallback, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { getOptions, getVcVenues } from './utils'
import { VenueCard } from './VenueCard'

interface Props {
  /**
   * The prefix used to fetch venue collections
   */
  vcPrefix: string
  /**
   * Page title in meta
   */
  title: string
  /**
   * Logo next to the MrYum Logo at the top
   */
  logo: React.ReactElement
  /**
   * URL of the header image
   */
  headerImageUrl: string
  /**
   * URL of the header image
   */
  isHeaderFullSizeOnDesktop?: boolean
  /**
   * Placeholder for select field
   */
  selectPlaceholder: string
  /**
   * Label for all collection
   */
  allCollectionsLabel: string
  /**
   * If to exclude MrYum's logo
   */
  exludeMrYumLogo?: boolean
  /**
   * Color of the buttons (Open now and Closed)
   */
  venueButtonColor?: string
}

export const DirectoryPage = ({
  vcPrefix,
  title,
  logo,
  headerImageUrl,
  selectPlaceholder,
  allCollectionsLabel,
  children,
  exludeMrYumLogo = false,
  venueButtonColor,
  isHeaderFullSizeOnDesktop,
}: PropsWithChildren<Props>) => {
  const { push, query, pathname } = useRouter()
  const [submitting, setSubmitting] = useState(false)
  const headerRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)

  const [{ data }] = useDirectoryVenueCollectionsQuery({
    variables: {
      vcPrefix,
      vcSlug: query.vcSlug ? String(query.vcSlug) : undefined,
    },
  })

  const vcOptions = getOptions(data!)
  const vcVenues = getVcVenues(data!)
  const selectedVcOption = find(vcOptions, { id: String(query.vcSlug) })

  const handleChange = useCallback<(changes: any) => void>(
    async ({ selectedItem }) => {
      const venueSlug = selectedItem?.id
      setSubmitting(true)
      const directoryBasePath = pathname.split('/').slice(1, 3).join('/')
      if (venueSlug === 'all') {
        await push(`/${directoryBasePath}`, undefined, { shallow: true })
      } else {
        await push(`/${directoryBasePath}/${venueSlug}`, undefined, {
          shallow: true,
        })
      }

      setSubmitting(false)

      if (gridRef.current && navRef.current && headerRef.current) {
        window.scrollTo({
          top:
            gridRef.current.offsetTop -
            (navRef.current?.offsetHeight + headerRef.current.offsetHeight),
          behavior: 'smooth',
        })
      }
    },
    [push, pathname, setSubmitting],
  )
  const headerImageSize = isHeaderFullSizeOnDesktop
    ? 'relative w-full h-0 pt-[calc(9/16*100%)] bg-surface-subtle'
    : 'relative w-full h-0 pt-[calc(9/16*100%)] md:pt-[calc(1/3*100%)] bg-surface-subtle'

  if (!data) return null
  return (
    <div>
      <Meta title={title} />
      <div
        ref={headerRef}
        className="sticky top-0 z-[2] flex h-[54px] items-center justify-center border-b bg-surface text-foreground md:h-[68px]"
        style={{
          boxShadow: '0 2px 4px rgba(0,0,0, 0.1)',
        }}
      >
        <div>
          <div className="flex items-center justify-center">
            {logo}
            {!exludeMrYumLogo && (
              <>
                <div className="px-4">
                  <CloseIcon />
                </div>
                <Logo height={30} color="black" />
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto flex min-h-[calc(100vh-54px)] max-w-5xl flex-col justify-between md:min-h-[calc(100vh-68px)]">
        <div className="bg-surface text-foreground">
          <div className={headerImageSize}>
            <div className="absolute inset-0">
              <LazyImage src={headerImageUrl} />
            </div>
          </div>

          <div className="px-4 pt-6 text-center">{children}</div>
        </div>

        <div
          ref={navRef}
          className="sticky top-[54px] z-[1] border-b bg-surface p-4 md:top-[68px]"
        >
          <div className="mx-auto max-w-[480px]">
            <Select
              options={[
                { id: 'all', label: allCollectionsLabel },
                ...vcOptions,
              ]}
              value={selectedVcOption}
              placeholder={selectPlaceholder}
              onChange={handleChange}
              isLoading={submitting}
              clearable={false}
              className={cn('border-black', {
                '!bg-white': !!selectedVcOption,
                '!bg-black !text-white': !selectedVcOption,
              })}
            />
          </div>
        </div>

        <div
          ref={gridRef}
          className="grid flex-1 grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-8 bg-surface-subtle p-4 md:p-8"
        >
          {vcVenues.map((vcVenue) => (
            <VenueCard
              key={vcVenue.id}
              vcVenue={vcVenue}
              query={query}
              color={venueButtonColor}
            />
          ))}
        </div>
        <div className="flex h-[54px] items-center justify-center bg-black text-white my-body-sm md:h-[68px]">
          <FormattedMessage
            defaultMessage="© me&u {year} - All rights reserved"
            id="2l6iwr"
            values={{ year: new Date().getFullYear() }}
          />
        </div>
      </div>
    </div>
  )
}
